import { useEffect, useMemo, useState } from 'react';
import type { DelegateOption, UserDelegation, UserDelegationInput } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Loader2 } from 'lucide-react';

export interface OutOfOfficeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The time away being changed; null to plan new time away. */
  cover: UserDelegation | null;
  colleagues: DelegateOption[];
  /** Today (YYYY-MM-DD) at the user's company — the date the server checks against. */
  today: string;
  saving: boolean;
  /** Resolves null when saved (the dialog closes), else the reason to show. */
  onSave: (input: UserDelegationInput, id?: number) => Promise<string | null>;
}

const REASON_MAX = 500;

/**
 * CR-5889: plan or change time away — who covers, the first and last day
 * away, and an optional reason. The server's own rules (rights, overlaps,
 * a colleague who is away too) come back as the message under the form.
 */
export function OutOfOfficeDialog({
  open, onOpenChange, cover, colleagues, today, saving, onSave,
}: OutOfOfficeDialogProps) {
  const [delegate, setDelegate] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDelegate(cover ? String(cover.delegate_user) : '');
    setStartDate(cover ? cover.start_date : today);
    setEndDate(cover ? cover.end_date : '');
    setReason(cover?.reason ?? '');
    setProblem(null);
  }, [open, cover, today]);

  // Time away already running keeps its first day: that day is history now.
  const started = cover !== null && cover.start_date < today;

  const options = useMemo(() => {
    const list = colleagues.map((c) => ({ value: String(c.id), label: c.name }));
    // Someone picked earlier who no longer qualifies still shows by name; the
    // save then explains why they can't cover.
    if (cover && !list.some((o) => o.value === String(cover.delegate_user))) {
      list.unshift({ value: String(cover.delegate_user), label: cover.delegate_user_name });
    }
    return list;
  }, [colleagues, cover]);

  const check = (): string | null => {
    if (!delegate) return 'Pick who will cover for you.';
    if (!startDate || !endDate) return 'Choose the first and last day you are away.';
    if (!started && startDate < today) return "The first day can't be in the past.";
    if (endDate < startDate) return 'The last day must be on or after the first day.';
    if (endDate < today) return "The last day can't be in the past.";
    return null;
  };

  const handleSave = async () => {
    const local = check();
    setProblem(local);
    if (local) return;
    const input: UserDelegationInput = {
      delegate_user: Number(delegate),
      start_date: startDate,
      end_date: endDate,
      reason: reason.trim(),
    };
    const refused = await onSave(input, cover?.id);
    if (refused) setProblem(refused);
    else onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{cover ? 'Change time away' : 'Plan time away'}</DialogTitle>
          <DialogDescription>
            From the first day to the last, your notifications and approvals go to the
            colleague you pick instead of you.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <div className="space-y-2">
            <Label htmlFor="ooo-delegate">Covered by</Label>
            {options.length === 0 ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Nobody at this site holds all of your approval rights and has permissions
                here, so nobody can cover for you yet. Ask your admin to give a colleague the
                same rights and permissions at this site.
              </p>
            ) : (
              <>
                <SearchableSelect
                  id="ooo-delegate"
                  options={options}
                  value={delegate}
                  onChange={setDelegate}
                  placeholder="Pick a colleague"
                  disabled={saving}
                  inlineList
                />
                <p className="text-xs text-muted-foreground">
                  Only colleagues who hold the same approval rights as you and have
                  permissions at this site are listed.
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ooo-start">First day away</Label>
              <Input
                id="ooo-start"
                type="date"
                value={startDate}
                min={started ? undefined : today}
                disabled={saving || started}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ooo-end">Last day away</Label>
              <Input
                id="ooo-end"
                type="date"
                value={endDate}
                min={startDate > today ? startDate : today}
                disabled={saving}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ooo-reason">Reason (optional)</Label>
            <Textarea
              id="ooo-reason"
              value={reason}
              maxLength={REASON_MAX}
              rows={2}
              placeholder="e.g. Annual leave"
              disabled={saving}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Only you see the reason. The colleague covering sees the dates.
            </p>
          </div>

          {problem && <p className="text-sm text-red-600" role="alert">{problem}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || options.length === 0}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
